import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetConsentStatus, useRecordConsent } from 'dataQueries'

import { AxiosError } from 'axios'
import { consentTypesToRecord, isConsentRequired as isConsentRequiredRule, missingConsentTypes as missingConsentTypesRule } from 'utils/embedConsent/consentRules'
import constate from 'constate'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'
import { useEmbedConfig } from './EmbedConfig.context'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * GDPR/T&C consent state for the Propstack embed (ISSUE-2017).
 *
 * On embed entry the consent status is fetched for the user's workspace on the PROPSTACK channel
 * (ISSUE-2012 endpoints). While any document is missing/stale the value-proposition overlay and the
 * summary consent checkbox are shown, and `ensureConsentRecorded` must succeed before an order can
 * be placed. Outside the embed (or for non-clients — the endpoints are client-only, and the embed
 * admin path is a test path) the whole context is inert: nothing is fetched, nothing is required.
 */
export const [EmbedConsentProvider, useEmbedConsent] = constate(() => {
  const { isEmbed } = useEmbedConfig()
  const { roles } = useAuth0()
  const { userPropertyWorkspaceId } = useUserData()

  // Requires the workspace: the status GET is enabled only when it is known, so without this the
  // query would never settle and `isConsentResolved` would hang the controller on its spinner.
  const isConsentApplicable = isEmbed && roles.isClient && !!userPropertyWorkspaceId

  const consentStatusQuery = useGetConsentStatus(userPropertyWorkspaceId, 'PROPSTACK', isConsentApplicable)
  const recordConsent = useRecordConsent()

  const missingConsentTypes = useMemo(
    () => missingConsentTypesRule(consentStatusQuery.data),
    [consentStatusQuery.data]
  )

  /** True once the display decision can be made (query settled, or consent not applicable at all). */
  const isConsentResolved = !isConsentApplicable || consentStatusQuery.isSuccess || consentStatusQuery.isError
  /**
   * True once the embed's first screen is genuinely decided (ISSUE-3218): isConsentResolved is also true while the
   * workspace is still unknown (consent not applicable yet), a render bound for the spinner, not a screen.
   */
  const isFirstScreenSettled = !isEmbed || !roles.isClient || (!!userPropertyWorkspaceId && isConsentResolved)

  // Shared with PF v2 so the two embeds cannot disagree about when consent is due
  // (incl. the fail-closed rule on an unreadable status).
  const isConsentRequired = isConsentRequiredRule({
    isApplicable: isConsentApplicable,
    hasError: consentStatusQuery.isError,
    missing: missingConsentTypes,
  })

  useEffect(() => {
    if (!consentStatusQuery.isError) return
    logger.warn('Embed consent status fetch failed — failing closed (consent re-asked)', consentStatusQuery.error, {
      context: { component: 'EmbedConsent', action: 'consentStatusFetchFailed' },
    })
  }, [consentStatusQuery.isError, consentStatusQuery.error])

  const [isConsentChecked, setIsConsentChecked] = useState(false)
  const [isValuePropositionDismissed, setIsValuePropositionDismissed] = useState(false)
  const dismissValueProposition = useCallback(() => setIsValuePropositionDismissed(true), [])

  /** The overlay shows once per embed session, only while consent is missing. */
  const showValueProposition = isConsentRequired && !isValuePropositionDismissed

  // Funnel denominator: fire once when the overlay first becomes visible, so its click-through
  // (against the per-card PURCHASE_FLOW_EMBED_OVERLAY_SERVICE_SELECTED events) is measurable.
  const overlayShownLoggedRef = useRef(false)
  useEffect(() => {
    if (!showValueProposition || overlayShownLoggedRef.current) return
    overlayShownLoggedRef.current = true
    // overlay_shown / consent_shown are constants by construction (ISSUE-4023): this only runs when the overlay
    // renders, which only happens while consent is missing. Sent so the spec's fields exist.
    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_EMBED_OVERLAY_SHOWN, { overlay_shown: true, consent_shown: true })
  }, [showValueProposition])

  /**
   * True while the final "Zustimmen und bestellen" CTA must stay disabled: consent is required and
   * the summary checkbox has not been ticked yet. Feeds `isRecapStepValid` so the existing
   * validityMap plumbing disables the footer CTA (no parallel disable mechanism).
   */
  const isConsentBlockingOrder = isConsentRequired && !isConsentChecked

  /**
   * Records the missing consents before order placement. Resolves `true` when the order may
   * proceed: consent recorded, already up to date, or HTTP 409 (a concurrent duplicate — the
   * winning request already persisted the acceptance). Resolves `false` on any other failure so
   * the caller blocks the order.
   */
  const ensureConsentRecorded = useCallback(async (): Promise<boolean> => {
    if (!isConsentRequired) return true

    const consentTypes = consentTypesToRecord(missingConsentTypes)

    try {
      await recordConsent.mutateAsync({
        workspaceId: userPropertyWorkspaceId,
        channel: 'PROPSTACK',
        consentTypes,
      })
      logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_EMBED_CONSENT_RECORDED, { consentTypes })
      return true
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        // Benign: consent was recorded concurrently. Realign the cached status and continue.
        consentStatusQuery.refetch()
        return true
      }
      logger.warn('Recording embed consent failed — order placement blocked', error, {
        context: { component: 'EmbedConsent', action: 'recordConsentFailed' },
      })
      return false
    }
  }, [isConsentRequired, missingConsentTypes, recordConsent, userPropertyWorkspaceId, consentStatusQuery])

  return {
    isConsentResolved,
    isFirstScreenSettled,
    isConsentRequired,
    isConsentBlockingOrder,
    isConsentChecked,
    setIsConsentChecked,
    showValueProposition,
    dismissValueProposition,
    ensureConsentRecorded,
    isConsentRecording: recordConsent.isPending,
  }
})
